import { AdjustmentsHorizontalIcon } from '@heroicons/react/20/solid'
import { useLocation } from 'react-router-dom'
import { Plus as PlusIcon } from 'iconoir-react'
import { compact } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { Search } from '../Search'
import { isSystemPipelineId } from 'app/components/action-center/systemPipelines'
import { Button } from 'app/components/ui/button'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import EditPipeline from './EditPipeline'
import { EditStages } from './EditStages'
import { StageColumn } from './StageColumn'
import { useSettings } from 'app/hooks/useSettings'
import { usePipelineTaskCounts } from 'app/hooks/usePipelineTaskCounts'
import { usePipelineCommContext } from 'app/hooks/usePipelineCommContext'
import type { ContactJson } from 'app/types/entities'
import type { PipelineType, StageType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { getListCardConfig } from 'lib/listCardConfig'
import type { ListCardConfig } from 'lib/listCardConfig'
import { isFieldEligible } from 'lib/listCardFields'
import { PipelineCardCustomization } from './PipelineCardCustomization'
import { PipelineLocationSelector } from './PipelineLocationSelector'

const AddNewStage = ({ pipeline, updatePipelineStage, deleteStage }) => (
  <button
    className="m-2 flex h-11 min-w-fit items-center space-x-2 text-heading-xs text-minimal"
    onClick={() => {
      showModal(
        <EditPipeline
          index={pipeline?.stages?.length || -1}
          updatePipelineStage={updatePipelineStage}
          deleteStage={deleteStage}
          type="ADD"
        />,
        {
          title: `Add new stage`,
          isFullScreen: false,
          removePadding: true,
          useRadix: true,
        },
      )
    }}
  >
    <PlusIcon className="h-5 w-5" />
    <span>Add new stage</span>
  </button>
)

export function PipelineLayout({
  activeSubpage,
  contacts,
  pipeline,
  contactHandler,
  updatePipelineStage,
  updateContactStage,
  deleteStage,
  addContactToPipeline,
  removeContact,
}: {
  activeSubpage: string
  contacts: ContactJson[]
  pipeline: PipelineType
  contactHandler: (contactId: string) => void
  updatePipelineStage: (stage: StageType, index: number) => Promise<void | { error?: string }>
  deleteStage: (name: string) => void
  updateContactStage: (contactId: string, stage: StageType) => void
  addContactToPipeline: (contact: ContactJson, stage: StageType) => void
  removeContact: (contactId: string) => void
}): ReactElement {
  const location = useLocation()
  const isMobile = useIsMobile()
  const visibilitySettings = `columnVisibility-${
    activeSubpage ? `${location.pathname}${activeSubpage}` : location.pathname
  }`
  const [visibleStages, setVisibleStages] = useState<(StageType & { visible: boolean })[]>(
    () => pipeline?.stages?.map((stage) => ({ ...stage, visible: true })) ?? [],
  )
  const [settings, setSettings] = useSettings(visibilitySettings)

  const [rawCardConfig, setCardConfig] = useSettings<ListCardConfig>(`pipeline-card-config`)
  const cardConfig = getListCardConfig(rawCardConfig, isFieldEligible)
  const needsTasks = cardConfig.slots.meta.includes(`tasks`)
  const contactIds = contacts.map((c) => c.id!).filter(Boolean)
  const taskCounts = usePipelineTaskCounts(contactIds, needsTasks)
  const needsComm = cardConfig.showCommunications
  const commContext = usePipelineCommContext(contacts, needsComm)

  const openCardCustomizer = () =>
    showModal(
      <PipelineCardCustomization
        initialConfig={cardConfig}
        onSave={(next) => setCardConfig(`pipeline-card-config`, next)}
      />,
      { title: `Customize card`, isFullScreen: false, removePadding: true, useRadix: true },
    )

  useEffect(() => {
    if (!pipeline?.stages) {
      setVisibleStages([])
      return
    }
    const stagesWithSettings = pipeline.stages.map((stage) => {
      const stageSettings = settings?.[stage.name]
      return {
        ...stage,
        visible: stageSettings?.visible ?? true,
        order: stageSettings?.order ?? stage.order,
      }
    })
    setVisibleStages(stagesWithSettings)
  }, [settings, pipeline, visibilitySettings])

  const handleStagesConfiguration = async (visibleStages: (StageType & { visible: boolean })[]) => {
    setVisibleStages(visibleStages)
    const stageSettings = visibleStages.reduce(
      (acc, stage) => ({
        ...acc,
        [stage.name]: {
          visible: stage.visible,
          order: stage.order,
        },
      }),
      {},
    )
    await setSettings(visibilitySettings, stageSettings)
  }
  return (
    <div x-component-name="PipelineLayout" className="flex h-full w-full flex-col space-y-3 overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Search />
          <PipelineLocationSelector />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="tertiary" size="sm" onClick={openCardCustomizer} aria-label="Customize card">
            {isMobile ? <AdjustmentsHorizontalIcon className="h-4 w-4" /> : `Customize card`}
          </Button>
          <EditStages stages={visibleStages} onActionTriggered={handleStagesConfiguration} />
        </div>
      </div>
      {/* snap-x is the mobile one-stage-at-a-time swipe; md:snap-none keeps
          desktop trackpad/shift-scroll free-scrolling as it was. */}
      <div className="flex min-h-0 grow snap-x overflow-x-auto overflow-y-hidden md:snap-none">
        {visibleStages &&
          compact(visibleStages).length > 0 &&
          visibleStages
            ?.filter(({ visible }) => visible)
            ?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            ?.map((stage, index) => (
              <StageColumn
                key={`${stage.name}-${stage.color}-${stage.order}`}
                stage={{ id: pipeline.id!, ...stage }}
                index={index}
                stageContacts={compact(
                  contacts?.filter((contact) =>
                    contact?.pipelines?.find((p) => pipeline.id === p.id && stage.name === p.stage),
                  ),
                )}
                addContactToPipeline={addContactToPipeline}
                contactHandler={contactHandler}
                updatePipelineStage={updatePipelineStage}
                deleteStage={deleteStage}
                updateContactStage={updateContactStage}
                removeContact={removeContact}
                config={cardConfig}
                taskCounts={taskCounts}
                commContext={commContext}
              />
            ))}
        {/* A system pipeline's stages are the Action Center boards' columns; adding one here
            would land nowhere the board reads from, so the affordance is absent rather than
            offered and refused. */}
        {!isSystemPipelineId(pipeline.id) && (
          <AddNewStage pipeline={pipeline} updatePipelineStage={updatePipelineStage} deleteStage={deleteStage} />
        )}
      </div>
    </div>
  )
}
